import type {
  CustomerWorkbookNotificationOut,
  Paginated_WorkbookSummaryOut_,
  WorkbookAssignmentOut,
  WorkbookCustomerClaimOut,
  WorkbookCustomerProgressOut,
  WorkbookCustomerStatisticsOut,
  WorkbookDetailOut,
  WorkbookNotificationContextOut,
  WorkbookPathAliasOut,
  WorkbookReviewerOut,
  WorkbookReviewOut,
  WorkbookWorkingIdentityIn,
} from './generated'
import { WorkbooksService } from './generated'
import {
  requireWorkbookBlob,
  resolveWorkbookRequest,
  toApiCellEdits,
  toApiRowDeletions,
  toApiRowInsertions,
} from './workbookRequests'
import type {
  CellEdit,
  WorkbookRowDeletion,
  WorkbookRowInsertion,
} from '@/components/customer/workbookGrid'

export interface NativeWorkbookIdentity {
  generationId: string
  correctionRevisionId: string | null
  effectiveHash: string
  customerVersion?: number
  customerEdit?: boolean
}

export interface NativeWorkbookQuery {
  scopeId?: string
  assignee?: 'all' | 'mine' | 'unassigned'
  assigneeIds?: string[]
  includeUnassigned?: boolean
  review?: 'all' | 'needs_review' | 'ready_for_customer' | 'customer_visible'
  customerStatus?: 'all' | 'needs_review' | 'in_progress' | 'done'
  search?: string
  sort?:
    | 'created_at'
    | '-created_at'
    | 'updated_at'
    | '-updated_at'
    | 'display_name'
    | '-display_name'
    | 'progress'
    | '-progress'
    | 'sent_to_customer'
    | '-sent_to_customer'
  page?: number
  pageSize?: number
}

export interface NativeNotificationRequest {
  retryRecipients?: string[] | null
  note?: string | null
}

function expectedState(identity: NativeWorkbookIdentity) {
  return {
    expected_generation_id: identity.generationId,
    expected_correction_revision_id: identity.correctionRevisionId,
    expected_effective_hash: identity.effectiveHash,
  }
}

function identityColumnCreation(identities: WorkbookWorkingIdentityIn[]) {
  if (!identities.length) return {}
  // The server admits creation while allowing recovery and accepted replay after rollback.
  return { working_identities: identities }
}

export function fetchNativeCustomerStatistics(
  projectId: number,
): Promise<WorkbookCustomerStatisticsOut> {
  return WorkbooksService.customerStatisticsProjectsProjectIdWorkbooksCustomerStatisticsGet({
    projectId,
  })
}

export function updateNativeCustomerProgress(
  projectId: number,
  workbookId: string,
  identity: NativeWorkbookIdentity,
  status: 'needs_review' | 'in_progress' | 'done',
): Promise<WorkbookCustomerProgressOut> {
  return WorkbooksService.updateCustomerProgressProjectsProjectIdWorkbooksWorkbookIdCustomerProgressPut(
    {
      projectId,
      workbookId,
      requestBody: {
        expected_generation_id: identity.generationId,
        expected_effective_hash: identity.effectiveHash,
        expected_version: identity.customerVersion ?? 0,
        status,
      },
    },
  )
}

export function listNativeWorkbooks(
  projectId: number,
  query: NativeWorkbookQuery = {},
  signal?: AbortSignal,
): Promise<Paginated_WorkbookSummaryOut_> {
  return resolveWorkbookRequest(
    WorkbooksService.listWorkbooksProjectsProjectIdWorkbooksGet({ projectId, ...query }),
    signal,
  )
}

export function resolveNativeWorkbookPath(
  projectId: number,
  path: string,
): Promise<WorkbookPathAliasOut> {
  return WorkbooksService.resolveWorkbookPathProjectsProjectIdWorkbooksResolveGet({
    projectId,
    path,
  })
}

export function updateNativeWorkbookCustomerClaim(
  projectId: number,
  workbookId: string,
  assigneeUserId: string | null,
  expectedVersion: number,
): Promise<WorkbookCustomerClaimOut> {
  return WorkbooksService.updateWorkbookCustomerClaimProjectsProjectIdWorkbooksWorkbookIdCustomerClaimPut(
    {
      projectId,
      workbookId,
      requestBody: { assignee_user_id: assigneeUserId, expected_version: expectedVersion },
    },
  )
}

export function listNativeWorkbookCustomerReviewers(
  projectId: number,
): Promise<WorkbookReviewerOut[]> {
  return WorkbooksService.listWorkbookCustomerReviewersProjectsProjectIdWorkbooksCustomerReviewersGet(
    { projectId },
  )
}

export function fetchNativeWorkbook(
  projectId: number,
  workbookId: string,
  signal?: AbortSignal,
): Promise<WorkbookDetailOut> {
  return resolveWorkbookRequest(
    WorkbooksService.getWorkbookProjectsProjectIdWorkbooksWorkbookIdGet({
      projectId,
      workbookId,
    }),
    signal,
  )
}

export async function fetchNativeWorkbookBlob(
  projectId: number,
  workbookId: string,
  snapshotToken: string,
  version: 'original' | 'effective' = 'effective',
  signal?: AbortSignal,
): Promise<Blob> {
  const value = await resolveWorkbookRequest(
    WorkbooksService.getWorkbookContentProjectsProjectIdWorkbooksWorkbookIdContentGet({
      projectId,
      workbookId,
      snapshotToken,
      version,
    }),
    signal,
  )
  return requireWorkbookBlob(value)
}

export function saveNativeWorkbookCorrections(
  projectId: number,
  workbookId: string,
  identity: NativeWorkbookIdentity,
  edits: CellEdit[],
  idempotencyId: string,
  rowInsertions: WorkbookRowInsertion[] = [],
  rowDeletions: WorkbookRowDeletion[] = [],
  identityColumns: WorkbookWorkingIdentityIn[] = [],
): Promise<WorkbookDetailOut> {
  return WorkbooksService.saveWorkbookCorrectionProjectsProjectIdWorkbooksWorkbookIdCorrectionPut({
    projectId,
    workbookId,
    requestBody: {
      ...expectedState(identity),
      ...identityColumnCreation(identityColumns),
      ...(identity.customerVersion !== undefined
        ? { expected_customer_version: identity.customerVersion }
        : {}),
      ...(identity.customerEdit ? { customer_edit: true } : {}),
      idempotency_id: idempotencyId,
      edits: toApiCellEdits(edits),
      ...(rowInsertions.length ? { row_insertions: toApiRowInsertions(rowInsertions) } : {}),
      ...(rowDeletions.length ? { row_deletions: toApiRowDeletions(rowDeletions) } : {}),
    },
  })
}

export async function renderNativeWorkbookCorrections(
  projectId: number,
  workbookId: string,
  identity: NativeWorkbookIdentity,
  edits: CellEdit[],
  rowInsertions: WorkbookRowInsertion[] = [],
  rowDeletions: WorkbookRowDeletion[] = [],
  identityColumns: WorkbookWorkingIdentityIn[] = [],
): Promise<Blob> {
  const value =
    await WorkbooksService.renderWorkbookCorrectionProjectsProjectIdWorkbooksWorkbookIdCorrectionRenderPost(
      {
        projectId,
        workbookId,
        requestBody: {
          ...expectedState(identity),
          ...identityColumnCreation(identityColumns),
          ...(identity.customerVersion !== undefined
            ? { expected_customer_version: identity.customerVersion }
            : {}),
          ...(identity.customerEdit ? { customer_edit: true } : {}),
          edits: toApiCellEdits(edits),
          ...(rowInsertions.length ? { row_insertions: toApiRowInsertions(rowInsertions) } : {}),
          ...(rowDeletions.length ? { row_deletions: toApiRowDeletions(rowDeletions) } : {}),
        },
      },
    )
  return requireWorkbookBlob(value)
}

export function resetNativeWorkbookCorrections(
  projectId: number,
  workbookId: string,
  identity: NativeWorkbookIdentity,
): Promise<WorkbookDetailOut> {
  return WorkbooksService.resetWorkbookCorrectionProjectsProjectIdWorkbooksWorkbookIdCorrectionResetPost(
    {
      projectId,
      workbookId,
      requestBody: { ...expectedState(identity), confirmed: true },
    },
  )
}

export function fetchNativeWorkbookReview(
  projectId: number,
  workbookId: string,
  identity: NativeWorkbookIdentity,
  signal?: AbortSignal,
): Promise<WorkbookReviewOut> {
  return resolveWorkbookRequest(
    WorkbooksService.getWorkbookReviewStatusProjectsProjectIdWorkbooksWorkbookIdReviewStatusGet({
      projectId,
      workbookId,
      expectedGenerationId: identity.generationId,
      expectedEffectiveHash: identity.effectiveHash,
    }),
    signal,
  )
}

export function setNativeWorkbookReviewStatus(
  projectId: number,
  workbookId: string,
  identity: NativeWorkbookIdentity,
  expectedRevision: number,
  status: 'needs_review' | 'ready_for_customer',
): Promise<WorkbookReviewOut> {
  return WorkbooksService.setWorkbookReviewStatusProjectsProjectIdWorkbooksWorkbookIdReviewStatusPut(
    {
      projectId,
      workbookId,
      requestBody: {
        expected_generation_id: identity.generationId,
        expected_effective_hash: identity.effectiveHash,
        expected_revision: expectedRevision,
        status,
      },
    },
  )
}

export function verifyNativeWorkbookRows(
  projectId: number,
  workbookId: string,
  identity: NativeWorkbookIdentity,
  rowIndices: number[],
): Promise<WorkbookReviewOut> {
  return WorkbooksService.setWorkbookVerifiedRowsProjectsProjectIdWorkbooksWorkbookIdVerifiedRowsPut(
    {
      projectId,
      workbookId,
      requestBody: {
        expected_generation_id: identity.generationId,
        expected_effective_hash: identity.effectiveHash,
        row_indices: rowIndices,
      },
    },
  )
}

export function listNativeWorkbookReviewers(projectId: number): Promise<WorkbookReviewerOut[]> {
  return WorkbooksService.listWorkbookReviewersProjectsProjectIdWorkbooksReviewersGet({ projectId })
}

export function updateNativeWorkbookAssignment(
  projectId: number,
  workbookId: string,
  assigneeUserId: string | null,
  expectedVersion: number,
): Promise<WorkbookAssignmentOut> {
  return WorkbooksService.updateWorkbookAssignmentProjectsProjectIdWorkbooksWorkbookIdAssignmentPut(
    {
      projectId,
      workbookId,
      requestBody: {
        assignee_user_id: assigneeUserId,
        expected_version: expectedVersion,
      },
    },
  )
}

export function renameNativeWorkbook(
  projectId: number,
  workbookId: string,
  displayName: string,
  expectedVersion: number,
): Promise<WorkbookDetailOut> {
  return WorkbooksService.renameWorkbookLifecycleProjectsProjectIdWorkbooksWorkbookIdLifecyclePatch(
    {
      projectId,
      workbookId,
      requestBody: {
        display_name: displayName,
        expected_version: expectedVersion,
      },
    },
  )
}

export function archiveNativeWorkbook(
  projectId: number,
  workbookId: string,
  expectedVersion: number,
): Promise<WorkbookDetailOut> {
  return WorkbooksService.archiveWorkbookLifecycleProjectsProjectIdWorkbooksWorkbookIdLifecycleDelete(
    { projectId, workbookId, expectedVersion },
  )
}

export function fetchNativeNotificationContext(
  projectId: number,
  workbookId: string,
): Promise<WorkbookNotificationContextOut> {
  return WorkbooksService.getWorkbookNotificationContextProjectsProjectIdWorkbooksWorkbookIdNotificationContextGet(
    { projectId, workbookId },
  )
}

export function sendNativeCustomerNotifications(
  projectId: number,
  workbookId: string,
  effectiveHash: string,
  request: NativeNotificationRequest = {},
): Promise<CustomerWorkbookNotificationOut> {
  return WorkbooksService.sendWorkbookCustomerNotificationsProjectsProjectIdWorkbooksWorkbookIdCustomerNotificationsPost(
    {
      projectId,
      workbookId,
      requestBody: {
        expected_effective_hash: effectiveHash,
        retry_recipients: request.retryRecipients,
        note: request.note,
      },
    },
  )
}

export async function fetchNativeSourceCardBlob(
  projectId: number,
  workbookId: string,
  generationId: string,
  rowId: string,
  effectiveHash: string,
  signal?: AbortSignal,
): Promise<Blob> {
  const value = await resolveWorkbookRequest(
    WorkbooksService.getWorkbookSourceCardProjectsProjectIdWorkbooksWorkbookIdGenerationsGenerationIdRowsRowIdSourceCardGet(
      {
        projectId,
        workbookId,
        generationId,
        rowId,
        expectedEffectiveContentHash: effectiveHash,
      },
    ),
    signal,
  )
  return requireWorkbookBlob(value)
}

export async function fetchNativeInputSourceCardBlob(
  projectId: number,
  workbookId: string,
  generationId: string,
  sourceFileVersionId: string,
  effectiveHash: string,
  signal?: AbortSignal,
): Promise<Blob> {
  const value = await resolveWorkbookRequest(
    WorkbooksService.getWorkbookInputSourceCardProjectsProjectIdWorkbooksWorkbookIdGenerationsGenerationIdInputsSourceFileVersionIdSourceCardGet(
      {
        projectId,
        workbookId,
        generationId,
        sourceFileVersionId,
        expectedEffectiveContentHash: effectiveHash,
      },
    ),
    signal,
  )
  return requireWorkbookBlob(value)
}
